import { makeAutoObservable } from 'mobx';
import React from 'react';
import { AccountInterface } from 'type/accountType';
import { accountApiService } from '../service/api/accountApiService';
import {
  ADMIN_MENU_ITEMS,
  CONTENT_MENU_ITEMS,
  CONTRACT_MENU_ITEMS,
  CP_MENU_KEY_LIST,
  INGEST_MENU_ITEMS,
  META_DATA_STRUCTURE_MENU_ITEMS,
  PERMISSION_MENU_ITEMS,
  REVENUE_MENU_ITEMS,
  SYNC_MENU_ITEMS,
  TOOLS_MENU_ITEMS,
  VENDOR_MENU_ITEMS,
  VIETNAM_BASE_MENU_KEY_LIST,
} from 'constants/menu';
import {
  VIETNAM_MENU_PREFIX,
  VIETNAM_UPLOAD_ACTIONS,
  VIETNAM_UPLOAD_NEW_MENU,
  VIETNAM_UPLOADS_MENU,
} from 'constants/permission';

const SUPER_ADMIN_USER_ID = '1';

/**
 * 도메인이 한정된 계정의 권한 prefix.
 *
 * 권한이 **전부** 이 prefix 안에 있으면 그 도메인 전용 계정이다. INGEST/SYNC auto-push
 * (아래 `getMenus` 참고)를 이런 계정에는 적용하지 않는다.
 */
const SCOPED_MENU_PREFIXES = ['vendor-', VIETNAM_MENU_PREFIX];

const collectMenuKeys = (items: { key?: React.Key }[] | undefined): string[] =>
  (items ?? [])
    .map(item => (typeof item?.key === 'string' ? item.key : ''))
    .filter((k): k is string => !!k);

const ALL_MENU_KEYS: string[] = [
  ...collectMenuKeys(CONTENT_MENU_ITEMS as { key?: React.Key }[] | undefined),
  ...collectMenuKeys(META_DATA_STRUCTURE_MENU_ITEMS as { key?: React.Key }[] | undefined),
  ...collectMenuKeys(CONTRACT_MENU_ITEMS as { key?: React.Key }[] | undefined),
  ...collectMenuKeys(REVENUE_MENU_ITEMS as { key?: React.Key }[] | undefined),
  ...collectMenuKeys(INGEST_MENU_ITEMS as { key?: React.Key }[] | undefined),
  ...collectMenuKeys(SYNC_MENU_ITEMS as { key?: React.Key }[] | undefined),
  ...collectMenuKeys(TOOLS_MENU_ITEMS as { key?: React.Key }[] | undefined),
  ...collectMenuKeys(VENDOR_MENU_ITEMS as { key?: React.Key }[] | undefined),
  ...collectMenuKeys(ADMIN_MENU_ITEMS as { key?: React.Key }[] | undefined),
  ...collectMenuKeys(PERMISSION_MENU_ITEMS as { key?: React.Key }[] | undefined),
];

class AccountStore {
  private account: AccountInterface | null = null;
  private projectId: number | null = null;
  private role: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  public reset = (): void => {
    this.account = null;
    this.projectId = null;
    this.role = null;
  };

  public async init(): Promise<void> {
    const account = await accountApiService.fetchAccount();
    this.account = account;
    const { project_id: projectId, role } = await accountApiService.getUserProjectRoles(
      Number(this.account?.id)
    );
    this.projectId = projectId;
    this.role = role;
  }

  public async fetchAccount(): Promise<AccountInterface> {
    if (this.account) {
      return this.account;
    }
    const account = await accountApiService.fetchAccount();
    const { project_id: projectId, role } = await accountApiService.getUserProjectRoles(
      Number(account.id)
    );
    this.projectId = projectId;
    this.role = role;
    this.account = account;
    return account;
  }

  public async update(account: AccountInterface): Promise<void> {
    await accountApiService.update(Number(account.id), account);
  }

  public async updatePassword(oldPassword: string, newPassword: string): Promise<void> {
    await accountApiService.updatePassword(oldPassword, newPassword);
  }

  public setAccount = (account: AccountInterface): void => {
    this.account = account;
  };

  public setProject = (projectId: number): void => {
    if (this.projectId) {
      this.projectId = projectId;
    } else {
      throw new Error('Account not found');
    }
  };

  public getAccount = (): AccountInterface | null => {
    return this.account;
  };

  public getProject = (): number | null => {
    return this.projectId;
  };

  public getRole = (): string | null => {
    return this.role;
  };

  public isStaff = (): boolean => {
    return this.account?.is_staff ?? false;
  };

  public isInit = (): boolean => {
    return this.account !== null;
  };

  public isSuperAdmin = (): boolean => {
    return String(this.account?.id ?? '') === SUPER_ADMIN_USER_ID;
  };

  /**
   * ODK 운영진(`admin` role) 여부.
   *
   * BE `authorization.permissions.is_admin`이 `UserRole.role.name === 'admin'`으로 판정한 결과를
   * 응답으로 받아 그대로 쓴다 — role 이름은 `permissions`(code 배열)에 실리지 않아 프론트가
   * 스스로 알 수 없고, `is_staff`는 모든 계정에 True라 판정에 쓸 수 없다.
   *
   * 주의: 이름이 `admin`인 role만 해당한다. `vietnam-admin` 같은 다른 role은 **false**다.
   */
  public isOdkAdmin = (): boolean => {
    if (this.isSuperAdmin()) return true;
    return Boolean(this.account?.is_admin);
  };

  public getMenus = (): string[] => {
    // Super admin (user_id=1): 권한과 무관하게 모든 메뉴 노출.
    // 단, vendor-download는 벤더 사용자 전용 다운로드 페이지이므로 super admin에게도 숨김.
    if (this.isSuperAdmin()) {
      return ALL_MENU_KEYS.filter(k => k !== 'vendor-download');
    }

    const permissions = Array.from(this.account?.permissions ?? []);
    const permissionList = permissions.map(permission => permission.split(':')[0]);

    // Vendor-only 사용자(vendor-* 권한만 보유)는 INGEST/SYNC auto-push 제외하고
    // 본인의 vendor 메뉴만 노출.
    const isVendorOnly =
      permissionList.length > 0 && permissionList.every(key => key.startsWith('vendor-'));
    if (isVendorOnly) {
      return permissionList;
    }

    // LG Channel Vietnam 전용 계정: BE allowlist가 /api/v1/vietnam-upload/ 와 /api/v1/accounts/
    // 뿐이라 다른 도메인 메뉴는 클릭 즉시 403이다. 계정 계열 메뉴 + 본인의 vietnam 메뉴만 노출.
    // (vendor-only 분기와 같은 모양 — 메뉴 키는 permission code에서 그대로 유도된다.)
    if (this.isLgChannelVietnam()) {
      const vietnamMenus = permissionList.filter(key => key.startsWith(VIETNAM_MENU_PREFIX));
      // 업로드 화면은 code에서 메뉴 키가 유도되지 않으므로 여기서 직접 넣는다.
      // VN 전용 계정에만 넣는 이유: 이 계정은 Import & Upload 화면(운영진용 진입점)이
      // allowlist 밖이라 볼 수 없어서 사이드바가 유일한 진입로다.
      const uploadMenu = this.canAddVietnamUpload() ? [VIETNAM_UPLOAD_NEW_MENU] : [];
      // VN 메뉴를 앞에 둔다 — getDefaultRoute()가 이 순서대로 첫 라우트를 찾으므로
      // 로그인 직후 착지 지점이 Profile이 아니라 Vietnam Video가 된다.
      return Array.from(new Set([...vietnamMenus, ...uploadMenu, ...VIETNAM_BASE_MENU_KEY_LIST]));
    }

    // INGEST/SYNC 메뉴는 permission code 로 관리되지 않아, 권한과 무관하게 넣어 준다
    // (2026-01 #326 — code 를 보고 판정하면 아무에게도 안 보인다).
    //
    // 그 탓에 도메인이 한정된 계정에도 딸려 들어갔다. 베트남 담당 계정(`vietnam-uploads:*`
    // 만 보유)에 KCP Ingest·Sync Monitor 가 보이던 것이 그 경우다. flag 가 꺼져 있어
    // 위 VN 분기를 타지 않고 여기까지 내려온다 — ODK 운영진에게는 flag 를 주지 않기 때문이다.
    //
    // 권한이 전부 스코프 prefix 안에 있으면 넣지 않는다. 일반 어드민(스코프 밖 권한을
    // 하나라도 가진 계정)은 종전과 같다.
    const isScopedAccount =
      permissionList.length > 0 &&
      permissionList.every(key => SCOPED_MENU_PREFIXES.some(prefix => key.startsWith(prefix)));
    if (!isScopedAccount) {
      permissionList.push(...INGEST_MENU_ITEMS!.map(item => item!.key as string));
      permissionList.push(...SYNC_MENU_ITEMS!.map(item => item!.key as string));
    }

    return this.account?.platform.id === 2
      ? permissionList.filter(item => CP_MENU_KEY_LIST.includes(item))
      : permissionList;
  };

  public getPermissions = (): string[] => {
    return this.account?.permissions ?? [];
  };

  public hasPermission = (menu: string, action: string): boolean => {
    // Super admin은 모든 권한 통과.
    if (this.isSuperAdmin()) return true;
    const permissions = Array.from(this.account?.permissions ?? []);
    const permission = `${menu}:${action}`;
    return permissions.includes(permission);
  };

  // --- LG Channel Vietnam 접근 규칙 (constants/permission.ts 참고) -----------------

  /**
   * LG Channel Vietnam 전용 계정 = user flag 또는 소속 company flag.
   * BE `common/scoping.is_lg_channel_vietnam()`과 같은 OR 판정이다.
   *
   * 이 계정은 BE `ScopedJWTAuthentication`이 `/api/v1/vietnam-upload/`·`/api/v1/accounts/`
   * 밖의 모든 API를 403으로 막으므로, 사이드바도 같은 범위로 축소한다.
   *
   * ODK 운영진에게는 flag를 부여하지 않는다 — BE `IsLgChannelVietnam`이 admin role을 flag 없이
   * 통과시키므로 필요가 없고, flag를 켜면 여기서 사이드바가 축소돼 버린다.
   */
  public isLgChannelVietnam = (): boolean => {
    if (this.isSuperAdmin()) return false;
    const account = this.account;
    if (!account) return false;
    return Boolean(account.is_lg_channel_vietnam || account.company?.is_lg_channel_vietnam);
  };

  /** 업로드 목록 조회 (`/vietnam-uploads`). 자기 company / 전 CP 범위는 서버가 정한다. */
  public canViewVietnamUploads = (): boolean =>
    this.hasPermission(VIETNAM_UPLOADS_MENU, VIETNAM_UPLOAD_ACTIONS.view);

  /** VN 업로드 화면 (`/assets/add-vn`). */
  public canAddVietnamUpload = (): boolean =>
    this.hasPermission(VIETNAM_UPLOADS_MENU, VIETNAM_UPLOAD_ACTIONS.add);

  /**
   * Ingest 실행. 권한 보유가 필요조건이고, 대상이 자기 company 레코드여야 한다 —
   * BE는 admin이라도 타 company 레코드에 404를 준다(쓰기 경로에는 우회가 없다).
   * 그래서 호출측에서 `record.company_slug === account.company.slug`를 함께 확인한다.
   */
  public canIngestVietnamUpload = (): boolean =>
    this.hasPermission(VIETNAM_UPLOADS_MENU, VIETNAM_UPLOAD_ACTIONS.ingest);

  /**
   * 전 CP 업로드 조회 자격. 인제스트 범위도 이 값을 따른다 — 서버가 두 판정을
   * 하나로 묶었기 때문이다(보이는 것만 실행 가능).
   *
   * `admin` role 만 가진 계정은 이 code 가 없어도 서버가 전 CP를 내려준다.
   * role 이름은 `permissions` 에 실리지 않아 프론트가 알 수 없으므로,
   * 호출측에서 응답에 타 company 가 섞였는지도 함께 보고 판단한다.
   */
  public canViewAllVietnamUploads = (): boolean =>
    this.hasPermission(VIETNAM_UPLOADS_MENU, VIETNAM_UPLOAD_ACTIONS.viewAll);
}

export const accountStore = new AccountStore();
